/* eslint-disable react/jsx-filename-extension */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

import { ALERT_TYPES, useDispatchAlert } from '@agilemd/ui-alert';
import { Icon } from '@agilemd/ui-icon';
import { equal, useBoolean } from '@agilemd/ui-utils';

import { getAllAnswers } from '../../data/answerSlice.js';
import { getProcedure } from '../../data/procedureSlice.js';
import { postResponse } from '../../services/api.js';
import { ICONS } from '../../utils/fontawesome.js';
import validate from '../../utils/validateInput.js';

const cache = {};
export function useSurpasInput() {
  const {
    isEmergency,
    isInpatient,
    needsICUBed,
    patientAge,
    patientHealthStatus,
    patientPhysicalStatus,
    practitionerSpecialty,
    tier
  } = useSelector(getAllAnswers, equal);
  const {
    cptCode,
    procedureEventRate,
    procedureRelativeValueUnits,
    procedureFrequency,
    hemorrhageFrequency,
    readmissionFrequency,
    notHomeFrequency
  } = useSelector(getProcedure, equal);

  return useMemo(
    () => ({
      cptCode,
      isEmergency,
      isInpatient,
      needsICUBed,
      patientAge,
      patientHealthStatus,
      patientPhysicalStatus,
      practitionerSpecialty,
      tier,
      procedureEventRate,
      procedureRelativeValueUnits,
      procedureFrequency,
      hemorrhageFrequency,
      readmissionFrequency,
      notHomeFrequency
    }),
    [
      cptCode,
      isEmergency,
      isInpatient,
      needsICUBed,
      patientAge,
      patientHealthStatus,
      patientPhysicalStatus,
      practitionerSpecialty,
      tier,
      procedureEventRate,
      procedureRelativeValueUnits,
      procedureFrequency,
      hemorrhageFrequency,
      readmissionFrequency,
      notHomeFrequency
    ]
  );
}
export function useSurpasResponse() {
  const dispatchAlert = useDispatchAlert();
  const input = useSurpasInput();
  const { value: isValid, setTrue, setFalse } = useBoolean();
  const { value: isSaving, setTrue: setSavingTrue, setFalse: setSavingFalse } = useBoolean();
  useEffect(() => {
    validate(input, err => {
      if (err) {
        setFalse();
      } else {
        setTrue();
      }
    });
  }, [input, setTrue, setFalse]);

  return {
    isSaving,
    isValid,
    saveResponse: useCallback(() => {
      if (!isValid) {
        return;
      }

      validate(input, (err, validated) => {
        if (err) {
          return;
        }

        setSavingTrue();
        postResponse(validated, { shouldRecordResponse: true })
          .then(() => {
            setSavingFalse();
            dispatchAlert({
              type: ALERT_TYPES.TEMPORARY,
              message: `Successfully saved response`,
              id: 'surpas-response-save-success',
              placement: 'bottom'
            });
          })
          .catch(error => {
            dispatchAlert({
              type: ALERT_TYPES.CRITICAL,
              message: `Failed to save response: ${error.message}`,
              id: 'surpas-response-save-failure',
              isDismissable: true
            });
          });
      });
    }, [input, isValid, setSavingFalse, setSavingTrue, dispatchAlert])
  };
}
const ERROR_AGE = 'Risk estimates cannot be provided for pediatric patients.';
export default function usePatientEventRate() {
  const input = useSurpasInput();
  const patientAgeError =
    input.patientAge && parseInt(input.patientAge, 10) < 18 ? ERROR_AGE : false;

  const [isLoading, setIsLoading] = useState(false);
  const [{ patientEventRate, errorMessage }, setResult] = useState({
    patientEventRate: null,
    errorMessage: null
  });
  useEffect(() => {
    if (patientAgeError) {
      return undefined;
    }

    let didUnMount = false;
    validate(input, (err, validated) => {
      // Had an error in validation
      if (err) {
        const nextErrorMessage = `${err.message} (${err.target})`;
        setResult(currentResult => {
          if (currentResult.errorMessage !== nextErrorMessage) {
            return { errorMessage: nextErrorMessage, patientEventRate: null };
          }

          return currentResult;
        });
        return;
      }

      const cacheKey = JSON.stringify(validated);

      // Is in cache so use that
      if (cache[cacheKey]) {
        setResult({
          patientEventRate: cache[cacheKey],
          errorMessage: null
        });
        return;
      }

      // Start fetching sequence
      setIsLoading(true);
      // Clear the previous error message if it exists
      setResult(currentResult => {
        if (currentResult.errorMessage) {
          return { errorMessage: null, patientEventRate: null };
        }

        return currentResult;
      });

      postResponse(validated)
        .then(({ data: body }) => {
          const nextPatientEventRate = Object.keys(body).reduce((out, k) => {
            out[k] = body[k] ? Number(body[k]) : null;

            return out;
          }, {});

          cache[cacheKey] = nextPatientEventRate;

          // still populate the cache if unmounted
          if (didUnMount) {
            return;
          }

          setResult({
            patientEventRate: nextPatientEventRate,
            errorMessage: null
          });

          setIsLoading(false);
        })
        .catch(e => {
          setResult({
            patientEventRate: null,
            errorMessage: e.message
          });
        });
    });

    return () => {
      didUnMount = true;
    };
  }, [input, patientAgeError]);
  const missingFields = [
    input.patientAge === null && 'Patient age',
    input.patientHealthStatus === null && 'Functional health status',
    input.tier === null && 'Tier',
    input.patientPhysicalStatus === null && 'ASA class',
    input.isInpatient === null && 'Planned hospitalization status',
    input.practitionerSpecialty === null && 'Practitioner specialty',
    input.isEmergency === null && 'Emergency procedure',
    input.needsICUBed === null && 'Should an ICU bed be scheduled for this patient after surgery'
  ].filter(Boolean);

  if (missingFields.length) {
    return {
      patientEventRate: null,
      isLoading: false,
      errorMessage: (
        <div className="surpas-output__body__required-fields">
          <div className="surpas-output__body__required-fields__icon">
            <Icon icon={ICONS.HINT} />
          </div>
          <div className="surpas-output__body__required-fields__text">
            <p className="surpas-output__body__required-fields__text__disclaimer">
              SURPAS is currently being evaluated under research protocol #15-1044. Clinicians are
              advised that this is approved for research and patient education purposes only.
            </p>
            <p>
              <strong>Please complete the following fields:</strong>
            </p>
            <ul>
              {missingFields.map(field => (
                <li key={field}>{field}</li>
              ))}
            </ul>
          </div>
        </div>
      )
    };
  }

  return {
    patientEventRate,
    errorMessage: patientAgeError || errorMessage,
    isLoading
  };
}
